// Exported pages for church app - reduces App.js size
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Calendar, Music, BookOpenText, HeartHandshake, Users, Bell,
  MessageCircle, Send, CheckCircle2, Church, Star, Heart, Eye,
  Zap, Sparkles, Sun, Moon, Camera, Image as ImageIcon, Upload,
  Mail, X, Trash2, Edit, MapPin, Clock, Plus, Play, Video, RefreshCw, AlertTriangle,
  UserPlus, LogIn, LogOut, Phone, User, Mic2, Guitar
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

// Fancy Button Component
const FancyButton = ({ children, onClick, className = "", variant = "default", type = "button" }) => {
  const variants = {
    default: "from-blue-500 via-purple-500 to-pink-500",
    success: "from-green-500 via-emerald-500 to-teal-500",
    warning: "from-amber-500 via-orange-500 to-red-500",
    info: "from-cyan-500 via-blue-500 to-indigo-500",
    royal: "from-indigo-600 via-purple-600 to-blue-600",
  };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      className={`px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl font-semibold text-white shadow-lg bg-gradient-to-r ${variants[variant]} hover:shadow-xl transition-all duration-300 min-h-[48px] touch-manipulation text-sm sm:text-base ${className}`}
      whileHover={{ scale: 1.02, boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)" }}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.button>
  );
};

// Section Card Component
const SectionCard = ({ icon: Icon, title, subtitle, children, gradient = "default", delay = 0 }) => {
  const gradients = {
    default: "from-slate-50 to-white",
    warm: "from-amber-50/50 to-orange-50/30",
    cool: "from-blue-50/50 to-cyan-50/30",
    success: "from-green-50/50 to-emerald-50/30",
    info: "from-blue-100 via-cyan-50 to-indigo-100",
    royal: "from-purple-50/50 to-pink-50/30",
    // Brand-aligned (Royal Purple + Gold) variants — Feb 2026 harmony pass (saturated)
    sanctuary: "from-violet-100 via-purple-50 to-amber-100",   // service schedule: worshipful purple↔cream
    parchment: "from-amber-100 via-yellow-50 to-orange-100",   // scripture / inspiration
    lavender:  "from-violet-100 via-purple-50 to-rose-100",    // get involved: warm + welcoming
    vesper:    "from-purple-100 via-fuchsia-50 to-rose-100", // connect with us: warm purple
    certificate: "from-amber-100 via-yellow-50 to-purple-100", // member registration: cream w/ purple corner
  };

  const iconColors = {
    default: "from-blue-500 to-purple-500",
    warm: "from-amber-500 to-orange-500",
    cool: "from-cyan-500 to-blue-500",
    success: "from-green-500 to-emerald-500",
    info: "from-blue-500 to-cyan-500",
    royal: "from-purple-500 to-pink-500",
    sanctuary: "from-violet-600 to-amber-500",
    parchment: "from-amber-500 to-yellow-600",
    lavender:  "from-violet-500 to-purple-600",
    vesper:    "from-purple-600 to-rose-500",
    certificate: "from-amber-600 to-violet-600",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 100 }}
      className={`rounded-2xl sm:rounded-3xl p-4 sm:p-6 bg-gradient-to-br ${gradients[gradient]} backdrop-blur-sm border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-500`}
    >
      <motion.div 
        className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: delay + 0.2 }}
      >
        <motion.div
          className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-r ${iconColors[gradient]} shadow-lg flex-shrink-0`}
          whileHover={{ scale: 1.1, rotate: 5 }}
        >
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </motion.div>
        <div className="min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-slate-800 truncate">{title}</h3>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500 truncate">{subtitle}</p>}
        </div>
      </motion.div>
      {children}
    </motion.div>
  );
};

// Helper functions
const formatDateRange = (start, end) => {
  if (!start) return "TBA";
  const s = new Date(start);
  const opts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  return s.toLocaleDateString('en-US', opts);
};

// ==================== EVENTS PAGE ====================
export function EventsPage({ events, setEvents, isAdmin, church }) {
  const [draft, setDraft] = useState({ title: "", start: "", end: "", location: "" });
  
  const add = async () => {
    if (!draft.title || !draft.start) return;
    try {
      const response = await axios.post(`${API}/events`, draft);
      setEvents(e => [response.data, ...e]);
      setDraft({ title: "", start: "", end: "", location: "" });
    } catch (error) {
      console.error("Error adding event:", error);
    }
  };

  const getEventStyle = (title) => {
    const lowerTitle = title?.toLowerCase() || '';
    if (lowerTitle.includes('sunday')) return { bg: 'from-amber-400 to-orange-500', icon: Sun, color: 'amber' };
    if (lowerTitle.includes('wednesday') || lowerTitle.includes('mercy')) return { bg: 'from-blue-400 to-indigo-500', icon: Moon, color: 'blue' };
    if (lowerTitle.includes('friday') || lowerTitle.includes('power')) return { bg: 'from-purple-400 to-pink-500', icon: Zap, color: 'purple' };
    if (lowerTitle.includes('thanksgiving')) return { bg: 'from-green-400 to-teal-500', icon: Heart, color: 'green' };
    return { bg: 'from-blue-400 to-purple-500', icon: Calendar, color: 'blue' };
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 sm:p-6 md:p-8 text-white shadow-2xl"
      >
        <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-white/10 rounded-full blur-3xl -translate-y-24 sm:-translate-y-32 translate-x-24 sm:translate-x-32"></div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <motion.div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-md flex-shrink-0 w-fit">
            <Calendar className="w-6 h-6 sm:w-8 sm:h-8" />
          </motion.div>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 sm:mb-1">Upcoming Events</h1>
            <p className="text-blue-100 text-sm sm:text-base">Join us for worship and fellowship</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/20 backdrop-blur-md">
            <Bell className="w-4 h-4" />
            <span className="text-sm font-medium">{events.length} Events</span>
          </div>
        </div>
      </motion.div>

      {isAdmin && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <input className="w-full border-2 border-purple-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-purple-400 transition-colors text-base min-h-[48px]" placeholder="Event title" value={draft.title} onChange={e=>setDraft(d=>({...d, title: e.target.value}))} />
            <input className="w-full border-2 border-purple-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-purple-400 transition-colors text-base min-h-[48px]" type="datetime-local" value={draft.start} onChange={e=>setDraft(d=>({...d, start: e.target.value}))} />
            <input className="w-full border-2 border-purple-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-purple-400 transition-colors text-base min-h-[48px]" type="datetime-local" value={draft.end} onChange={e=>setDraft(d=>({...d, end: e.target.value}))} />
            <input className="w-full border-2 border-purple-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-purple-400 transition-colors text-base min-h-[48px]" placeholder="Location" value={draft.location} onChange={e=>setDraft(d=>({...d, location: e.target.value}))} />
          </div>
          <FancyButton onClick={add} className="w-full">Create Event</FancyButton>
        </motion.div>
      )}
      
      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-100">
        {events.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <Calendar className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-slate-300" />
            <h3 className="text-base sm:text-lg font-semibold text-slate-700 mb-2">No upcoming events</h3>
            <p className="text-slate-500 text-sm sm:text-base">Check back soon for worship services</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {events.slice(0, 10).map((ev, index) => {
              const style = getEventStyle(ev.title);
              const EventIcon = style.icon;
              return (
                <motion.div key={ev.id} className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-slate-100 hover:shadow-xl transition-all" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                  <div className={`absolute left-0 top-0 bottom-0 w-1 sm:w-1.5 bg-gradient-to-b ${style.bg}`}></div>
                  <div className="p-3 sm:p-5 pl-4 sm:pl-6 flex items-start sm:items-center gap-3 sm:gap-4">
                    <div className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br ${style.bg} shadow-lg flex-shrink-0`}>
                      <EventIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-base sm:text-lg text-slate-800 line-clamp-2 sm:truncate">{ev.title}</h4>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-3 mt-1 text-xs sm:text-sm text-slate-600">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />{formatDateRange(ev.start, ev.end)}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />{ev.location || "TBA"}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== SERMONS PAGE ====================
export function SermonsPage({ sermons, setSermons, isAdmin }) {
  const [draft, setDraft] = useState({ title: "", speaker: "", date: "", link: "" });

  const add = async () => {
    if (!draft.title) return;
    try {
      const response = await axios.post(`${API}/sermons`, draft);
      setSermons(s => [response.data, ...s]);
      setDraft({ title: "", speaker: "", date: "", link: "" });
    } catch (error) {
      console.error("Error adding sermon:", error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 p-4 sm:p-6 md:p-8 text-white shadow-2xl">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <motion.div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-md w-fit"><BookOpenText className="w-6 h-6 sm:w-8 sm:h-8" /></motion.div>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 sm:mb-1">Sermon Library</h1>
            <p className="text-pink-100 text-sm sm:text-base">Messages that transform lives</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/20 backdrop-blur-md">
            <span className="text-sm font-medium">{sermons.length} Sermons</span>
          </div>
        </div>
      </motion.div>

      {isAdmin && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <input className="w-full border-2 border-pink-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-pink-400 text-base min-h-[48px]" placeholder="Sermon title" value={draft.title} onChange={e=>setDraft(d=>({...d, title: e.target.value}))} />
            <input className="w-full border-2 border-pink-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-pink-400 text-base min-h-[48px]" placeholder="Speaker" value={draft.speaker} onChange={e=>setDraft(d=>({...d, speaker: e.target.value}))} />
            <input className="w-full border-2 border-pink-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-pink-400 text-base min-h-[48px]" type="date" value={draft.date} onChange={e=>setDraft(d=>({...d, date: e.target.value}))} />
            <input className="w-full border-2 border-pink-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-pink-400 text-base min-h-[48px]" placeholder="Link (optional)" value={draft.link} onChange={e=>setDraft(d=>({...d, link: e.target.value}))} />
          </div>
          <FancyButton onClick={add} variant="warning" className="w-full">Add Sermon</FancyButton>
        </motion.div>
      )}

      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-100">
        {sermons.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <BookOpenText className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-slate-300" />
            <h3 className="text-base sm:text-lg font-semibold text-slate-700 mb-2">No sermons yet</h3>
            <p className="text-slate-500 text-sm sm:text-base">Check back soon for inspirational messages</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {sermons.slice(0, 12).map((s, index) => (
              <motion.div key={s.id} className="group p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 hover:shadow-lg transition-all" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex-shrink-0">
                    <BookOpenText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-slate-800 mb-1 truncate text-sm sm:text-base">{s.title}</h4>
                    <p className="text-xs sm:text-sm text-slate-600">{s.speaker}</p>
                    <p className="text-xs text-slate-500 mt-1">{s.date}</p>
                    {s.link && (
                      <a href={s.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs sm:text-sm text-purple-600 hover:text-purple-700">
                        <Play className="w-3 h-3 sm:w-4 sm:h-4" /> Watch/Listen
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== PRAYER PAGE ====================
export function PrayerPage({ prayers, setPrayers, isAuthenticated }) {
  const [draft, setDraft] = useState({ name: "", request: "" });

  const add = async () => {
    if (!draft.request) return;
    try {
      const response = await axios.post(`${API}/prayers`, draft);
      setPrayers(p => [response.data, ...p]);
      setDraft({ name: "", request: "" });
    } catch (error) {
      console.error("Error adding prayer:", error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 p-4 sm:p-6 md:p-8 text-white shadow-2xl">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <motion.div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-md w-fit"><HeartHandshake className="w-6 h-6 sm:w-8 sm:h-8" /></motion.div>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 sm:mb-1">Prayer Wall</h1>
            <p className="text-cyan-100 text-sm sm:text-base">Lifting each other up in prayer</p>
          </div>
        </div>
      </motion.div>

      <SectionCard icon={Send} title="Submit Prayer Request" subtitle="Share your prayer needs" gradient="cool">
        <div className="grid gap-3 sm:gap-4">
          <input className="w-full border-2 border-cyan-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-cyan-400 text-base min-h-[48px]" placeholder="Your name (optional)" value={draft.name} onChange={e=>setDraft(d=>({...d, name: e.target.value}))} />
          <textarea className="w-full border-2 border-cyan-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-cyan-400 resize-none text-base" placeholder="Your prayer request" rows={3} value={draft.request} onChange={e=>setDraft(d=>({...d, request: e.target.value}))} />
          <FancyButton onClick={add} variant="info" className="w-full">Submit Prayer Request</FancyButton>
        </div>
      </SectionCard>

      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <h3 className="text-xl font-bold text-slate-800 mb-6">Prayer Requests ({prayers.length})</h3>
        {prayers.length === 0 ? (
          <div className="text-center py-12">
            <HeartHandshake className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No prayer requests yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {prayers.slice(0, 10).map((p, index) => (
              <motion.div key={p.id} className="p-4 rounded-2xl bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-100" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                <p className="text-slate-700">{p.request}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm text-cyan-700 font-medium">{p.name || 'Anonymous'}</span>
                  <span className="text-xs text-slate-500">{p.date}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== GIVING PAGE ====================
export function GivingPage({ givingUrl, setChurch, isAdmin }) {
  const [editUrl, setEditUrl] = useState(givingUrl || '');
  const [isEditing, setIsEditing] = useState(false);

  const saveGivingUrl = async () => {
    try {
      await axios.put(`${API}/church-settings`, { givingUrl: editUrl });
      setChurch(prev => ({ ...prev, givingUrl: editUrl }));
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving giving URL:", error);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 p-8 text-white shadow-2xl">
        <div className="relative z-10 flex items-center gap-4">
          <motion.div className="p-4 rounded-2xl bg-white/20 backdrop-blur-md"><Heart className="w-8 h-8" /></motion.div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1">Give Online</h1>
            <p className="text-green-100">Support the work of the ministry</p>
          </div>
        </div>
      </motion.div>

      <SectionCard icon={Heart} title="Your Generosity Matters" subtitle="Every gift makes a difference" gradient="success">
        <div className="text-center py-8">
          <motion.div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-6" animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
            <Heart className="w-10 h-10 text-white" />
          </motion.div>
          <h3 className="text-xl font-bold text-slate-800 mb-4">Thank you for your generosity</h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">Your contributions help us continue spreading the Gospel and serving our community.</p>
          
          {givingUrl ? (
            <a href={givingUrl} target="_blank" rel="noopener noreferrer">
              <FancyButton variant="success" className="text-lg px-8">Give Now</FancyButton>
            </a>
          ) : (
            <p className="text-slate-500">Giving portal coming soon</p>
          )}
          
          {isAdmin && (
            <div className="mt-6 p-4 rounded-xl bg-slate-100">
              {isEditing ? (
                <div className="flex gap-2">
                  <input className="flex-1 border-2 border-emerald-200 rounded-xl px-4 py-2" placeholder="Enter giving URL" value={editUrl} onChange={e => setEditUrl(e.target.value)} />
                  <button onClick={saveGivingUrl} className="px-4 py-2 rounded-xl bg-emerald-500 text-white font-medium">Save</button>
                  <button onClick={() => setIsEditing(false)} className="px-4 py-2 rounded-xl bg-slate-300 text-slate-700 font-medium">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setIsEditing(true)} className="text-sm text-emerald-600 font-medium">Edit Giving URL</button>
              )}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

// ==================== TEAMS PAGE ====================
export function TeamsPage({ teams, setTeams, isAdmin }) {
  const [draft, setDraft] = useState({ name: "", description: "" });

  const add = async () => {
    if (!draft.name) return;
    try {
      const response = await axios.post(`${API}/teams`, draft);
      setTeams(t => [response.data, ...t]);
      setDraft({ name: "", description: "" });
    } catch (error) {
      console.error("Error adding team:", error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 p-4 sm:p-6 md:p-8 text-white shadow-2xl">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <motion.div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-md w-fit"><Users className="w-6 h-6 sm:w-8 sm:h-8" /></motion.div>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 sm:mb-1">Ministry Teams</h1>
            <p className="text-amber-100 text-sm sm:text-base">Serving together in unity</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/20 backdrop-blur-md">
            <span className="text-sm font-medium">{teams.length} Teams</span>
          </div>
        </div>
      </motion.div>

      {isAdmin && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <input className="w-full border-2 border-amber-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-amber-400 text-base min-h-[48px]" placeholder="Team name" value={draft.name} onChange={e=>setDraft(d=>({...d, name: e.target.value}))} />
            <input className="w-full border-2 border-amber-200 rounded-xl sm:rounded-2xl px-4 py-3 focus:border-amber-400 text-base min-h-[48px]" placeholder="Description" value={draft.description} onChange={e=>setDraft(d=>({...d, description: e.target.value}))} />
          </div>
          <FancyButton onClick={add} variant="warning" className="w-full">Add Team</FancyButton>
        </motion.div>
      )}

      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border border-slate-100">
        {teams.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-slate-300" />
            <h3 className="text-base sm:text-lg font-semibold text-slate-700 mb-2">No teams yet</h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {teams.map((team, index) => (
              <motion.div key={team.id} className="p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 hover:shadow-lg transition-all" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}>
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <div className="p-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex-shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm sm:text-base">{team.name}</h4>
                </div>
                {team.description && <p className="text-xs sm:text-sm text-slate-600">{team.description}</p>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== CHOIR PAGE ====================
const VOICE_PARTS = ['soprano', 'alto', 'tenor', 'bass'];
const EXPERIENCE_LEVELS = ['beginner', 'intermediate', 'experienced'];
const INSTRUMENTS = ['piano', 'organ', 'guitar', 'drums', 'saxophone', 'trumpet', 'violin', 'flute', 'none', 'other'];

export function ChoirPage({ rehearsals, setRehearsals, setlist, setSetlist, isAuthenticated }) {
  // Member login state
  const [choirMember, setChoirMember] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginSurname, setLoginSurname] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Form states for admin functions
  const [showRehearsalForm, setShowRehearsalForm] = useState(false);
  const [showSongForm, setShowSongForm] = useState(false);
  const [rehearsalDraft, setRehearsalDraft] = useState({ date: '', time: '', location: '', notes: '' });
  const [songDraft, setSongDraft] = useState({ title: '', composer: '', key: '', notes: '' });
  
  // Hymn states
  const [todaysHymns, setTodaysHymns] = useState(null);
  const [upcomingHymns, setUpcomingHymns] = useState([]);
  const [hymnLanguage, setHymnLanguage] = useState('english');
  const [loadingHymns, setLoadingHymns] = useState(true);
  const [expandedHymn, setExpandedHymn] = useState(null);

  // Check for saved member session
  useEffect(() => {
    const savedMember = localStorage.getItem('choirMember');
    if (savedMember) {
      setChoirMember(JSON.parse(savedMember));
    }
    loadHymnSelections();
  }, []);

  const loadHymnSelections = async () => {
    setLoadingHymns(true);
    try {
      const [todayRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/choir/service-hymns/today`),
        axios.get(`${API}/choir/service-hymns/upcoming`)
      ]);
      setTodaysHymns(todayRes.data);
      setUpcomingHymns(upcomingRes.data);
    } catch (error) {
      console.error('Error loading hymn selections:', error);
    }
    setLoadingHymns(false);
  };

  // Choir member login (phone + password — password set via welcome email)
  const handleMemberLogin = async () => {
    if (!loginPhone || !loginSurname) {
      setLoginError('Please enter your phone number and password');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      // Authenticate with the unified member-login endpoint.
      // The backend already enforces "choir-only" access (rejects with 403 if
      // the phone isn't on the choir roster), so we don't need to repeat that
      // check on the client — doing so caused false rejections when the
      // members.id and choir_approved_members.id didn't line up on legacy
      // production data.
      const loginRes = await axios.post(`${API}/auth/member/login`, {
        phone: loginPhone,
        password: loginSurname, // re-using same state field as the password input
      });
      const member = loginRes.data?.member;
      const sessionToken = loginRes.data?.token;
      if (!member?.id) throw new Error('Login response missing member id');

      const sessionMember = { ...member, token: sessionToken };
      setChoirMember(sessionMember);
      localStorage.setItem('choirMember', JSON.stringify(sessionMember));
      setShowLogin(false);
      setLoginPhone('');
      setLoginSurname('');
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Login failed. Check your phone and password.');
    }
    setLoginLoading(false);
  };

  const handleMemberLogout = () => {
    setChoirMember(null);
    localStorage.removeItem('choirMember');
  };

  const getServiceTypeLabel = (type) => {
    const labels = { sunday: 'Sunday Service', weekly: 'Weekday Service', new_moon: 'New Moon Service' };
    return labels[type] || type;
  };

  const currentHymns = todaysHymns?.hymns_english && hymnLanguage === 'english' 
    ? todaysHymns.hymns_english 
    : todaysHymns?.hymns_yoruba || [];

  const addRehearsal = async () => {
    if (!rehearsalDraft.date) return;
    try {
      const response = await axios.post(`${API}/choir/rehearsals`, rehearsalDraft);
      setRehearsals(r => [response.data, ...r]);
      setRehearsalDraft({ date: '', time: '', location: '', notes: '' });
      setShowRehearsalForm(false);
    } catch (error) {
      console.error("Error adding rehearsal:", error);
    }
  };

  const addSong = async () => {
    if (!songDraft.title) return;
    try {
      const response = await axios.post(`${API}/choir/songs`, songDraft);
      setSetlist(s => [response.data, ...s]);
      setSongDraft({ title: '', composer: '', key: '', notes: '' });
      setShowSongForm(false);
    } catch (error) {
      console.error("Error adding song:", error);
    }
  };

  const deleteRehearsal = async (id) => {
    try {
      await axios.delete(`${API}/choir/rehearsals/${id}`);
      setRehearsals(r => r.filter(reh => reh.id !== id));
    } catch (error) {
      console.error("Error deleting rehearsal:", error);
    }
  };

  const deleteSong = async (id) => {
    try {
      await axios.delete(`${API}/choir/songs/${id}`);
      setSetlist(s => s.filter(song => song.id !== id));
    } catch (error) {
      console.error("Error deleting song:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 p-8 text-white shadow-2xl"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-32 translate-x-32"></div>
        <div className="relative z-10 flex flex-wrap items-center gap-4">
          <motion.div className="p-4 rounded-2xl bg-white/20 backdrop-blur-md">
            <Music className="w-8 h-8" />
          </motion.div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">Choir Ministry</h1>
            <p className="text-violet-100">Making a joyful noise unto the Lord</p>
          </div>
          
          {/* Member Login/Status */}
          <div className="flex gap-2">
            {choirMember ? (
              <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/20 backdrop-blur-md">
                <div className="text-right">
                  <p className="text-sm font-medium">{choirMember.full_name}</p>
                  <p className="text-xs text-violet-200 capitalize">{choirMember.voice_part}</p>
                </div>
                <button onClick={handleMemberLogout} className="p-2 rounded-lg bg-white/20 hover:bg-white/30">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button onClick={() => setShowLogin(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 backdrop-blur-md hover:bg-white/30 transition-colors" data-testid="choir-header-member-login">
                <LogIn className="w-4 h-4" /> Member Login
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Member Login Modal */}
      <AnimatePresence>
        {showLogin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowLogin(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <LogIn className="w-5 h-5 text-purple-500" /> Choir Member Login
                </h3>
                <button onClick={() => setShowLogin(false)} className="p-2 rounded-lg hover:bg-slate-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-sm text-slate-500 mb-4">Enter your phone number and the <strong>Master Password</strong> emailed to you by the Main Admin.</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      placeholder="Enter your phone number"
                      data-testid="choir-login-phone"
                      className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-400 outline-none"
                      value={loginPhone}
                      onChange={e => setLoginPhone(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Master Password</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="password"
                      placeholder="Enter the master password"
                      data-testid="choir-login-password"
                      className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-400 outline-none"
                      value={loginSurname}
                      onChange={e => setLoginSurname(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleMemberLogin(); }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    The Main Admin emails the master password to every approved choir member. Ask an admin if you don't have it.
                  </p>
                </div>
                
                {loginError && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    {loginError}
                  </div>
                )}
                
                <button
                  onClick={handleMemberLogin}
                  disabled={loginLoading}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold hover:from-purple-600 hover:to-violet-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="choir-login-submit"
                >
                  {loginLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
                  {loginLoading ? 'Verifying...' : 'Login'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Today's Service Hymns */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500">
              <BookOpenText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">Today's Hymns</h3>
              {todaysHymns?.service_type && (
                <p className="text-sm text-slate-500">{getServiceTypeLabel(todaysHymns.service_type)} - {todaysHymns.service_date}</p>
              )}
            </div>
          </div>
          
          {/* Language Toggle */}
          <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl">
            <button
              onClick={() => setHymnLanguage('english')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                hymnLanguage === 'english' ? 'bg-white text-purple-700 shadow' : 'text-slate-600'
              }`}
            >
              English
            </button>
            <button
              onClick={() => setHymnLanguage('yoruba')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                hymnLanguage === 'yoruba' ? 'bg-white text-purple-700 shadow' : 'text-slate-600'
              }`}
            >
              Yorùbá
            </button>
          </div>
        </div>

        {loadingHymns ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-500" />
          </div>
        ) : !todaysHymns?.hymns_english?.length ? (
          <div className="text-center py-8">
            <BookOpenText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No hymns selected for today's service</p>
            <p className="text-sm text-slate-400 mt-1">Check back later or contact the choir admin</p>
          </div>
        ) : (
          <div className="space-y-3">
            {currentHymns.map((hymn, index) => (
              <motion.div
                key={hymn.number}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border border-purple-100 rounded-2xl overflow-hidden"
              >
                <div
                  onClick={() => setExpandedHymn(expandedHymn === hymn.number ? null : hymn.number)}
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-purple-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center text-white font-bold text-lg">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-purple-600 font-mono">Hymn No {hymn.number}</span>
                      <h4 className="font-bold text-slate-800 truncate">{hymn.title}</h4>
                    </div>
                    <p className="text-sm text-slate-500 truncate">{hymn.first_line}</p>
                  </div>
                  <div className={`transform transition-transform ${expandedHymn === hymn.number ? 'rotate-180' : ''}`}>
                    <Eye className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
                
                <AnimatePresence>
                  {expandedHymn === hymn.number && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="bg-purple-50 border-t border-purple-100"
                    >
                      <div className="p-4 space-y-4">
                        {hymn.verses?.map((verse, vIndex) => (
                          <p key={vIndex} className="text-slate-700 leading-relaxed whitespace-pre-line">
                            {verse.replace(/\*[^*]+\*/g, '')}
                          </p>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Services */}
      {upcomingHymns.length > 0 && (
        <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Upcoming Service Hymns</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingHymns.slice(0, 6).map((selection) => (
              <div key={selection.id} className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                <p className="font-semibold text-slate-800">{selection.service_date}</p>
                <p className="text-sm text-blue-600 mb-2">{getServiceTypeLabel(selection.service_type)}</p>
                <div className="flex flex-wrap gap-1">
                  {selection.hymn_numbers?.map(num => (
                    <span key={num} className="px-2 py-1 rounded bg-blue-200 text-blue-800 text-xs font-mono">
                      Hymn {num}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rehearsal Schedule - Only visible to logged-in choir members */}
      {choirMember ? (
        <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">Rehearsal Schedule</h3>
              <p className="text-sm text-slate-500">Welcome, {choirMember.full_name} {choirMember.surname}</p>
            </div>
          </div>

          {rehearsals?.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No rehearsals scheduled</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rehearsals?.map((reh, index) => (
                <motion.div 
                  key={reh.id} 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: index * 0.05 }} 
                  className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-100"
                >
                  <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-slate-800">{reh.date}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      {reh.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{reh.time}</span>}
                      {reh.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{reh.location}</span>}
                    </div>
                    {reh.notes && <p className="text-sm text-slate-500 mt-1">{reh.notes}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-3xl p-8 border border-purple-200 text-center">
          <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-purple-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">Rehearsal Schedule</h3>
          <p className="text-slate-600">Log in as a choir member (top-right) to view the rehearsal schedule.</p>
        </div>
      )}

      {/* Song Library */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500">
            <Music className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">Song Library</h3>
        </div>

        {setlist?.length === 0 ? (
          <div className="text-center py-8">
            <Music className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No songs in the library</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {setlist?.map((song, index) => (
              <motion.div 
                key={song.id} 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                transition={{ delay: index * 0.05 }} 
                className="p-4 rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-500">
                    <Music className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-bold text-slate-800">{song.title}</h4>
                </div>
                {song.composer && <p className="text-sm text-slate-600 mb-1">By: {song.composer}</p>}
                {song.key && <span className="inline-block px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">Key: {song.key}</span>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { FancyButton, SectionCard };
export { default as LoginPage } from './LoginPage';
